import React, { useEffect, useState, useContext, useCallback } from "react"
import QRCode from "qrcode.react"
import toastError from "../../errors/toastError"

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { X } from "lucide-react"
import api from "../../services/api"
import { SocketContext } from "../../context/Socket/SocketContext"
import {
  lModalQrDeveFechar,
  normalizarStatusConexao,
} from "../../hooks/useWhatsApps/whatsappStatusUtils"
import useStyles from "./styles"

const calcularTamanhoQr = (nLarguraJanela, nAlturaJanela, lMobile) => {
  if (lMobile) {
    const nMaxLargura = nLarguraJanela - 64
    const nMaxAltura = Math.floor(nAlturaJanela * 0.32)
    return Math.max(120, Math.min(200, nMaxLargura, nMaxAltura))
  }
  if (nLarguraJanela < 960) {
    return 220
  }
  return 256
}

const QrcodeModal = ({ open, onClose, whatsAppId, qrCodeInicial = "" }) => {
  const classes = useStyles()
  const theme = useTheme()
  const lMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [qrCode, setQrCode] = useState("")
  const [nTamanhoQr, setNTamanhoQr] = useState(() =>
    calcularTamanhoQr(window.innerWidth, window.innerHeight, window.innerWidth < 600),
  )

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    const aoRedimensionar = () => {
      setNTamanhoQr(calcularTamanhoQr(window.innerWidth, window.innerHeight, window.innerWidth < 600))
    }

    aoRedimensionar()
    window.addEventListener("resize", aoRedimensionar)
    return () => window.removeEventListener("resize", aoRedimensionar)
  }, [])

  const aplicarQrCode = useCallback((cValor) => {
    if (cValor != null && String(cValor).trim() !== "") {
      setQrCode(String(cValor))
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return undefined
    }
    aplicarQrCode(qrCodeInicial)
  }, [open, qrCodeInicial, aplicarQrCode])

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) {
        return
      }

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`)
        if (lModalQrDeveFechar(data)) {
          onClose()
          return
        }
        const cStatus = normalizarStatusConexao(data.status)
        if (cStatus === "QRCODE" && data.qrcode) {
          aplicarQrCode(data.qrcode)
        }
      } catch (err) {
        toastError(err)
      }
    }
    fetchSession()
  }, [whatsAppId, open, aplicarQrCode, onClose])

  useEffect(() => {
    if (!whatsAppId || !open) {
      return undefined
    }

    const companyId = localStorage.getItem("companyId")
    if (!companyId) {
      return undefined
    }

    const socket = socketManager.getSocket(companyId)
    const cCanalSessao = `company-${companyId}-whatsappSession`
    const nCanalId = Number(whatsAppId)

    const aoAtualizarSessao = (data) => {
      const nCanalEvento = Number(data?.session?.canalId ?? data?.session?.id)
      if (Number.isNaN(nCanalId) || nCanalEvento !== nCanalId) {
        return
      }

      if (data.action === "update" || data.action) {
        if (lModalQrDeveFechar(data.session)) {
          onClose()
          return
        }
        aplicarQrCode(data.session?.qrcode)
      }
    }

    socket.on(cCanalSessao, aoAtualizarSessao)

    return () => {
      socket.off(cCanalSessao, aoAtualizarSessao)
    }
  }, [whatsAppId, open, onClose, socketManager, aplicarQrCode])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={lMobile}
      scroll="paper"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h2" component="h2">
          Conectar WhatsApp
        </Typography>
        <IconButton className={classes.closeButton} onClick={onClose} aria-label="Fechar">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Paper elevation={0} className={classes.paper}>
          <div className={classes.qrWrapper}>
            {qrCode ? (
              <div className={classes.qrCanvas}>
                <QRCode value={qrCode} size={nTamanhoQr} />
              </div>
            ) : (
              <span className={classes.waiting}>Esperando pelo QR Code</span>
            )}
          </div>
          <div className={classes.instructions}>
            <Typography variant="h2" component="p" color="textPrimary" className={classes.titleDesktop}>
              Utilize o seu WhatsApp:
            </Typography>
            <Typography variant="body1" color="textPrimary" gutterBottom className={classes.step}>
              1 - Abra o WhatsApp no seu celular
            </Typography>
            <Typography variant="body1" color="textPrimary" gutterBottom className={classes.step}>
              2 - Toque em Mais opções no Android{" "}
              <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" inert="true">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path>
              </svg>{" "}
              ou em Configurações{" "}
              <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" inert="true">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"></path>
              </svg>{" "}
              no iPhone
            </Typography>
            <Typography variant="body1" color="textPrimary" gutterBottom className={classes.step}>
              3 - Toque em Dispositivos conectados e, em seguida, em Conectar dispositivos
            </Typography>
            <Typography variant="body1" color="textPrimary" gutterBottom className={classes.step}>
              4 - Aponte seu celular para essa tela para capturar o QR Code
            </Typography>
            <Typography component="p" className={classes.stepCompact}>
              Abra o WhatsApp → Dispositivos conectados → Conectar dispositivo → escaneie o QR Code acima com outro
              aparelho.
            </Typography>
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(QrcodeModal)
